import { useRouter } from "next/router";
import { useEffect, useMemo } from "react";

import { useConfiguration } from "~/contexts/configuration";
import useScript from "~/hooks/use-script";
import appRoutes from "~/utils/app-routes";
import powerReviewUtils, { PropsPowerReviewsCultureConfig } from "~/utils/power-reviews-utils";

type PropsProductReviews = {
  parent_backend_id: string | number;
  idReviewSnippet?: string;
  idCategorySnippet?: string;
  idReviewDisplay?: string;
};

declare global {
  var pwr: any;
}

export default function PowerReviewsUiScript({
  parent_backend_id,
  idReviewDisplay = "pr-reviewdisplay",
  idCategorySnippet = "pr-categorysnippet",
}: PropsProductReviews) {
  const router = useRouter();
  const { configuration } = useConfiguration();
  const pwrConfig: PropsPowerReviewsCultureConfig = useMemo(
    () => powerReviewUtils.getPwrConfiguration(configuration, router),
    [configuration, router]
  );

  const pwrUiLoaded = useScript(process.env.NEXT_PUBLIC_POWERREVIEWS_UI_URL);

  useEffect(() => {
    if (pwrUiLoaded && pwrConfig) {
      window.pwr =
        window.pwr ||
        function () {
          (pwr.q = pwr.q || []).push(arguments);
        };

      const pwrconfig = {
        merchant_group_id: pwrConfig.merchantGroupID,
        merchant_id: pwrConfig.merchantID,
        locale: pwrConfig.locale,
        api_key: pwrConfig.apiKey,
        page_id: parent_backend_id,
        review_wrapper_url: appRoutes.POWERREVIEWS_WRITE_A_REVIEW,
        components: {
          CategorySnippet: idCategorySnippet,
          ReviewDisplay: idReviewDisplay,
        },
      };
      pwr("render", pwrconfig);
    }
  }, [pwrUiLoaded, pwrConfig, parent_backend_id, idReviewDisplay, idCategorySnippet]);

  useEffect(() => {
    let observer: MutationObserver | undefined;

    if (!pwrConfig) {
      return;
    }

    const target = document.getElementById(idReviewDisplay);

    if (target) {
      observer = new MutationObserver(() => {
        document.querySelectorAll(".pr-review").forEach((review) => {
          const hasImage = review.querySelector(".pr-rd-image-container");
          if (hasImage) {
            review.classList.add("with-image");
          }
        });
      });

      observer.observe(target, { childList: true, subtree: true });
    }

    return () => observer?.disconnect();
  }, [idReviewDisplay, pwrConfig]);

  return <></>;
}
